import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { spaService } from '../services/spaService';
import LoadingSpinner from '../components/LoadingSpinner';
import { Tooltip } from 'primereact/tooltip';

const ManageHorseRegistrations = () => {
  const toast = useRef(null);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editingHorse, setEditingHorse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');

  const genderOptions = [
    { label: 'Mare', value: 'mare' },
    { label: 'Stallion', value: 'stallion' },
    { label: 'Gelding', value: 'gelding' }
  ];

  useEffect(() => {
    loadHorses();
  }, []);

  const loadHorses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await spaService.getHorseListAll();
      
      if (result.success) {
        setHorses(result.horses || []);
      } else {
        setError(result.error || 'Failed to load horses');
      }
    } catch (err) {
      console.error('Error loading horses:', err);
      setError(err.message || 'An error occurred while loading horses');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (horse) => {
    setEditingHorse({
      ...horse,
      dateOfBirth: horse.dateOfBirth ? new Date(horse.dateOfBirth) : null,
      gender: horse.gender?.toLowerCase() || null
    });
    setEditDialogVisible(true);
  };

  const handleEditInputChange = (field, value) => {
    setEditingHorse(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const confirmUpdate = () => {
    confirmDialog({
      message: 'Are you sure you wish to update this horse registration?',
      header: 'Confirm Update',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-primary',
      accept: handleUpdateHorse,
      reject: () => {}
    });
  };

  const confirmDelete = () => {
    if (!editingHorse) return;

    confirmDialog({
      message: `Are you sure you wish to delete ${editingHorse.horseName || 'this horse'}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: handleDeleteHorse,
      reject: () => {}
    });
  };

  const handleUpdateHorse = async () => {
    if (!editingHorse) return;

    // Basic validation
    if (!editingHorse.horseName || !editingHorse.dateOfBirth || !editingHorse.gender) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please fill in all required fields.',
        life: 3000
      });
      return;
    }

    setSaving(true);
    try {
      const result = await spaService.updateHorse({
        id: editingHorse.id,
        horseName: editingHorse.horseName,
        memberId: editingHorse.memberId,
        memberName: editingHorse.memberName,
        dateOfBirth: editingHorse.dateOfBirth instanceof Date 
          ? editingHorse.dateOfBirth.toISOString() 
          : editingHorse.dateOfBirth,
        breed: editingHorse.breed || '',
        gender: editingHorse.gender || '',
        height: editingHorse.height || '',
        colour: editingHorse.colour || '',
        breedSocietyRegistered: editingHorse.breedSocietyRegistered || false,
        breedSocietyDetails: editingHorse.breedSocietyDetails || ''
      });

      if (result.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Horse registration updated successfully.',
          life: 3000
        });
        setEditDialogVisible(false);
        setEditingHorse(null);
        loadHorses();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.error || 'Failed to update horse registration.',
          life: 3000
        });
      }
    } catch (err) {
      console.error('Error updating horse:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.message || 'An error occurred while updating the horse.',
        life: 3000
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHorse = async () => {
    if (!editingHorse) return;

    setSaving(true);
    try {
      const result = await spaService.deleteHorse(editingHorse.id, editingHorse.memberId);

      if (result.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Horse registration deleted successfully.',
          life: 3000
        });
        setEditDialogVisible(false);
        setEditingHorse(null);
        loadHorses();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.error || 'Failed to delete horse registration.',
          life: 3000
        });
      }
    } catch (err) {
      console.error('Error deleting horse:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.message || 'An error occurred while deleting the horse.',
        life: 3000
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (err) {
      return '-';
    }
  };

  const dateBodyTemplate = (rowData) => {
    return formatDate(rowData.dateOfBirth);
  };

  const heightBodyTemplate = (rowData) => {
    return rowData.height || '-';
  };

  const genderBodyTemplate = (rowData) => {
    var gender = rowData.gender;
    if (!gender) return '-';
    gender = gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
    
    const genderColors = {
      'Mare': 'info',
      'Gelding': 'success',
      'Stallion': 'warning'
    };
    
    return (
      <Tag 
        value={gender} 
        severity={genderColors[gender] || 'secondary'}
      />
    );
  };

  const colourBodyTemplate = (rowData) => {
    return rowData.colour || '-';
  };

  const breedBodyTemplate = (rowData) => {
    return rowData.breed || '-';
  };

  const memberNameBodyTemplate = (rowData) => {
    return rowData.memberName || '-';
  };

  const breedSocietyBodyTemplate = (rowData) => {
    if (rowData.breedSocietyRegistered) {
      return (
        <>           
        <span data-pr-tooltip={rowData.breedSocietyDetails || 'Registered'}        
            data-pr-position="top"
            data-pr-at="center top-15" className='bsoc'>  
          <i className="pi pi-check-circle" style={{ fontSize: '1.2rem', color: 'green' }}></i>
        </span>
        </>
      );
    }
    return (
      <i className="pi pi-times-circle" style={{ fontSize: '1.2rem', color: 'red' }}></i>
    );
  };

  const horseNameBodyTemplate = (rowData) => {
    return (
      <div className="font-semibold">{rowData.horseName || 'Unnamed Horse'}</div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <Button
        icon="pi pi-pencil"
        rounded
        text
        severity="secondary"
        tooltip={`Edit ${rowData.horseName || 'Horse'}`}
        tooltipOptions={{ position: 'top' }}
        onClick={() => openEditDialog(rowData)}
      />
    );
  };

  const editDialogFooter = (
    <div className="flex align-items-center gap-2 w-full">
      {editingHorse && (
        <Button
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          outlined
          onClick={confirmDelete}
          disabled={saving}
          className="mr-auto"
        />
      )}
      <Button
        label="Cancel"
        icon="pi pi-times"
        severity="secondary"
        onClick={() => setEditDialogVisible(false)}
        disabled={saving}
      />
      <Button
        label="Update"
        icon="pi pi-check"
        onClick={confirmUpdate}
        loading={saving}
      />
    </div>
  );

  const header = (
    <div className="flex justify-content-between align-items-center">
      <span className="p-input-icon-right">
        <i className="pi pi-search right-0 mr-2" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search horses..."
        />
      </span>
      <Button
        icon="pi pi-refresh"
        rounded
        text
        onClick={loadHorses}
        tooltip="Refresh"
        tooltipOptions={{ position: 'left' }}
      />
    </div>
  );

  if (loading) {
    return <LoadingSpinner message="Loading all horse registrations..." />;
  }

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />
      <Tooltip target=".bsoc" />
      <div className="se-card">
        <div className="se-card-header">
          <h2>Manage Horse Registrations</h2>
        </div>
        <div className="se-card-content">
          <p className="text-600 mb-3">
            View and manage all horse registrations across all members.
          </p>

          {error && (
            <Message 
              severity="error" 
              text={error}
              className="mb-3 w-full"
            />
          )}

          {!error && horses.length === 0 && (
            <Message 
              severity="info" 
              text="No horse registrations found."
              className="mb-3 w-full"
            />
          )}

          {!error && horses.length > 0 && (
            <DataTable 
              value={horses} 
              responsiveLayout="scroll" 
              className="mt-3"
              stripedRows
              paginator
              rows={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              emptyMessage="No horses found"
              sortField="horseName"
              sortOrder={1}
              globalFilter={globalFilter}
              header={header}
            >
              <Column 
                field="horseName" 
                header="Horse Name" 
                body={horseNameBodyTemplate}
                sortable
                filter
                filterPlaceholder="Search by name"
              />
              <Column 
                field="memberName" 
                header="Member Name" 
                body={memberNameBodyTemplate}
                sortable
                className="hidden-mobile" 
                headerClassName="hidden-mobile"
              />
              <Column 
                field="dateOfBirth" 
                header="DOB" 
                body={dateBodyTemplate} 
                sortable 
                className="hidden-mobile" 
                headerClassName="hidden-mobile"
              />
              <Column 
                field="height" 
                header="Height" 
                body={heightBodyTemplate}
                className="hidden-mobile" 
                headerClassName="hidden-mobile"
              />
              <Column 
                field="gender" 
                header="Gender" 
                body={genderBodyTemplate}
                sortable
                className="hidden-mobile" 
                headerClassName="hidden-mobile"
              />
              <Column 
                field="colour" 
                header="Colour" 
                body={colourBodyTemplate}
                className="hidden-mobile" 
                headerClassName="hidden-mobile"
              />
              <Column 
                field="breed" 
                header="Breed" 
                body={breedBodyTemplate}
                sortable
              />
              <Column 
                field="breedSocietyRegistered" 
                header="Breed Society" 
                body={breedSocietyBodyTemplate}
                align="center"
                className="hidden-mobile" 
                headerClassName="hidden-mobile"
              />
              <Column 
                header="" 
                body={actionBodyTemplate}
                style={{ width: '4rem' }}
                align="center"
              />
            </DataTable>
          )}
        </div>
      </div>

      {/* Edit Horse Dialog */}
      <Dialog
        header={`Edit Horse: ${editingHorse?.horseName || ''}`}
        visible={editDialogVisible}
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '641px': '95vw' }}
        onHide={() => setEditDialogVisible(false)}
        footer={editDialogFooter}
        modal
      >
        {editingHorse && (
          <div className="p-fluid">
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="se-form-group">
                  <label htmlFor="edit-horseName" className="se-form-label">
                    Horse Name <span className="se-form-required">*</span>
                  </label>
                  <InputText
                    id="edit-horseName"
                    value={editingHorse.horseName || ''}
                    onChange={(e) => handleEditInputChange('horseName', e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div className="se-form-group">
                  <label htmlFor="edit-dateOfBirth" className="se-form-label">
                    Date of Birth <span className="se-form-required">*</span>
                  </label>
                  <Calendar
                    id="edit-dateOfBirth"
                    value={editingHorse.dateOfBirth}
                    onChange={(e) => handleEditInputChange('dateOfBirth', e.value)}
                    dateFormat="dd/mm/yy"
                    className="w-full"
                    maxDate={new Date()}
                    yearRange="1990:2026"
                  />
                </div>
              </div>
            </div>

            <div className="grid">
              <div className="col-12 md:col-4">
                <div className="se-form-group">
                  <label htmlFor="edit-gender" className="se-form-label">
                    Gender <span className="se-form-required">*</span>
                  </label>
                  <Dropdown
                    id="edit-gender"
                    value={editingHorse.gender}
                    options={genderOptions}
                    onChange={(e) => handleEditInputChange('gender', e.value)}
                    placeholder="Select gender"
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="se-form-group">
                  <label htmlFor="edit-height" className="se-form-label">
                    Height
                  </label>
                  <InputText
                    id="edit-height"
                    value={editingHorse.height || ''}
                    onChange={(e) => handleEditInputChange('height', e.target.value)}
                    placeholder="e.g. 15.2hh"
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="se-form-group">
                  <label htmlFor="edit-colour" className="se-form-label">
                    Colour
                  </label>
                  <InputText
                    id="edit-colour"
                    value={editingHorse.colour || ''}
                    onChange={(e) => handleEditInputChange('colour', e.target.value)}
                    placeholder="e.g. Bay, Chestnut"
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="se-form-group">
              <label htmlFor="edit-breed" className="se-form-label">
                Breed
              </label>
              <InputText
                id="edit-breed"
                value={editingHorse.breed || ''}
                onChange={(e) => handleEditInputChange('breed', e.target.value)}
                placeholder="e.g. Arabian, Thoroughbred"
                className="w-full"
              />
            </div>

            <div className="se-form-group">
              <div className="flex align-items-center">
                <Checkbox
                  inputId="edit-breedSocietyRegistered"
                  checked={editingHorse.breedSocietyRegistered || false}
                  onChange={(e) => handleEditInputChange('breedSocietyRegistered', e.checked)}
                />
                <label htmlFor="edit-breedSocietyRegistered" className="ml-2 cursor-pointer">
                  Breed Society Registered
                </label>
              </div>
            </div>

            {editingHorse.breedSocietyRegistered && (
              <div className="se-form-group">
                <label htmlFor="edit-breedSocietyDetails" className="se-form-label">
                  Breed Society Registration Details
                </label>
                <InputText
                  id="edit-breedSocietyDetails"
                  value={editingHorse.breedSocietyDetails || ''}
                  onChange={(e) => handleEditInputChange('breedSocietyDetails', e.target.value)}
                  placeholder="Registration number and society name"
                  className="w-full"
                />
              </div>
            )}

            <div className="se-form-group mt-3">
              <label className="se-form-label text-500">Member Name</label>
              <div className="text-600">{editingHorse.memberName || '-'}</div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ManageHorseRegistrations;
